import { useMutation, useQueryClient } from "@tanstack/react-query"
import React, { useEffect, useState } from "react"

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  toast,
} from "@atlant/ui"

import { FormUsersSelect } from "#components/form-query/form-users-select.tsx"
import { graphql } from "#gql"
import type { AircraftSource } from "#gql/graphql.ts"
import { queryKeys } from "#lib/query-keys.ts"
import { client } from "#utils/graphql-client.ts"

const AircraftSetKamsDocument = graphql(/* GraphQL */ `
  mutation AircraftSetKeyAccountManagers(
    $input: MutationAircraftSetKeyAccountManagersInput!
  ) {
    aircraftSetKeyAccountManagers(input: $input)
  }
`)

interface AssignKamModalProps {
  source: AircraftSource
  aircraftId: string
  trigger?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultUserIds?: string[]
}

export const AssignKamModal = ({
  source,
  aircraftId,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultUserIds = [],
}: AssignKamModalProps) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const [userIds, setUserIds] = useState(defaultUserIds)
  const defaultKey = [...defaultUserIds].sort().join()
  const queryClient = useQueryClient()

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = (v: boolean) => {
    if (isControlled) controlledOnOpenChange?.(v)
    else setInternalOpen(v)
  }

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () =>
      client.request(AircraftSetKamsDocument, {
        input: { aircraftId, userIds, source },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.aircraft.detail(aircraftId),
      })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.aircraft.all(),
      })
      setOpen(false)
    },
  })

  useEffect(() => {
    if (open) setUserIds(defaultKey ? defaultKey.split(",") : [])
  }, [open, defaultKey])

  const isChanged = [...userIds].sort().join() !== defaultKey

  const handleSubmit = () => {
    toast.promise(mutateAsync() as Promise<unknown>, {
      loading: "Saving KAMs...",
      success: "KAMs updated!",
    })
  }

  const handleOpenChange = (v: boolean) => setOpen(v)

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Key Account Managers</DialogTitle>
          <DialogDescription>
            Select the team members who manage this aircraft
          </DialogDescription>
        </DialogHeader>

        <FormUsersSelect
          value={userIds}
          onChange={setUserIds}
          label="KAMs"
          placeholder="Select users"
        />

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" className="h-10 flex-1">
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={handleSubmit}
            disabled={!isChanged || isPending}
            className="h-10 flex-1"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
